import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AppstoreOutlined, DownOutlined, EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useDismissibleLayer } from '../hooks/useDismiss';
import {
  zoneCollapseStorageKey,
  zoneUnreadCount,
  type ZoneChannelGroup as Group,
} from './zoneRailModel';
import styles from './ChannelRail.module.css';

interface Props {
  group: Group;
  accountId: string;
  guildId: string;
  currentChannelId: string | null;
  onCreateChannel?: (zoneId: string) => void;
  onManageZone?: (zoneId: string, mode: 'rename' | 'delete') => void;
  children: ReactNode;
}

const readCollapsed = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    // A blocked browser preference store must not prevent channel navigation.
    return false;
  }
};

export const ZoneChannelGroup = ({
  group,
  accountId,
  guildId,
  currentChannelId,
  onCreateChannel,
  onManageZone,
  children,
}: Props) => {
  const { t, i18n } = useLingui();
  const key = zoneCollapseStorageKey(accountId, guildId, group.id);
  const [preferredCollapsed, setPreferredCollapsed] = useState(() => readCollapsed(key));
  const [explicitlyCollapsedSelection, setExplicitlyCollapsedSelection] = useState<string | null>(
    null,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const selected =
    currentChannelId !== null && group.channels.some((channel) => channel.id === currentChannelId);
  const temporarilyRevealed =
    preferredCollapsed && selected && explicitlyCollapsedSelection !== currentChannelId;
  const collapsed = preferredCollapsed && !temporarilyRevealed;
  const zoneId = group.id;
  const accessibleName = t`Zone: ${group.name}`;
  const unread = zoneUnreadCount(group.channels);

  useEffect(() => setExplicitlyCollapsedSelection(null), [currentChannelId]);
  useLayoutEffect(() => {
    if (!menuOpen) return;
    const trigger = menuTriggerRef.current!.getBoundingClientRect();
    const menu = menuRef.current!.getBoundingClientRect();
    setMenuPosition({
      left: Math.max(8, Math.min(trigger.right - menu.width, window.innerWidth - menu.width - 8)),
      top: Math.max(8, Math.min(trigger.bottom, window.innerHeight - menu.height - 8)),
    });
  }, [menuOpen]);
  useEffect(() => {
    if (menuOpen) menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [menuOpen]);
  useDismissibleLayer({
    active: menuOpen,
    within: [menuRef, menuTriggerRef],
    onEscape: () => {
      setMenuOpen(false);
      menuTriggerRef.current?.focus();
    },
    onOutsidePress: () => setMenuOpen(false),
  });

  const toggle = () => {
    const next = !collapsed;
    setPreferredCollapsed(next);
    setExplicitlyCollapsedSelection(next && selected ? currentChannelId : null);
    try {
      window.localStorage.setItem(key, String(next));
    } catch {
      // The in-memory preference remains usable when browser storage is blocked.
    }
  };

  return (
    <section
      className={styles.zoneGroup}
      aria-label={accessibleName}
      data-zone-kind="zone"
      data-zone-id={zoneId}
    >
      <div className={styles.zoneHeading}>
        <button
          type="button"
          className={styles.zoneToggle}
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={accessibleName}
        >
          <DownOutlined aria-hidden="true"
            className={`${styles.zoneChevron} ${collapsed ? styles.zoneChevronCollapsed : ''}`}
          />
          <AppstoreOutlined className={styles.zoneKind} aria-hidden="true" />
          <span className={styles.zoneName}>{group.name}</span>
        </button>
        {collapsed && unread > 0 && (
          <span
            className={`unread-badge ${styles.zoneUnread}`}
            aria-label={t`${unread} unread in ${accessibleName}`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        {onCreateChannel && (
          <button
            type="button"
            className={styles.zoneAction}
            onClick={() => onCreateChannel(zoneId)}
            aria-label={t`Create channel in ${accessibleName}`}
            title={t`Create channel in ${accessibleName}`}
          >
            <PlusOutlined aria-hidden="true" />
          </button>
        )}
        {onManageZone && (
          <>
            <button
              ref={menuTriggerRef}
              type="button"
              className={styles.zoneAction}
              onClick={() => setMenuOpen((value) => !value)}
              aria-label={t`Manage ${accessibleName}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <EllipsisOutlined aria-hidden="true" />
            </button>
            {menuOpen &&
              createPortal(
                <div
                  ref={menuRef}
                  className={styles.zoneMenu}
                  style={menuPosition}
                  role="menu"
                  aria-label={t`Manage ${accessibleName}`}
                  onKeyDown={(event) => {
                    const items = [
                      ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
                        '[role="menuitem"]',
                      ),
                    ];
                    const current = items.indexOf(document.activeElement as HTMLButtonElement);
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                      event.preventDefault();
                      items[
                        (current + (event.key === 'ArrowDown' ? 1 : items.length - 1)) %
                          items.length
                      ].focus();
                    }
                    if (event.key === 'Home' || event.key === 'End') {
                      event.preventDefault();
                      items[event.key === 'Home' ? 0 : items.length - 1].focus();
                    }
                  }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onManageZone(zoneId, 'rename');
                    }}
                  >
                    <Trans>Rename Zone</Trans>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onManageZone(zoneId, 'delete');
                    }}
                  >
                    <Trans>Delete Zone</Trans>
                  </button>
                </div>,
                document.body,
              )}
          </>
        )}
      </div>
      {!collapsed && (
        <>
          {temporarilyRevealed && (
            <p className={styles.revealNote}>
              <Trans>Expanded for the selected channel</Trans>
            </p>
          )}
          {group.channels.length === 0 ? (
            <div className={styles.zoneEmpty}>
              <Trans>No visible channels</Trans>
            </div>
          ) : (
            children
          )}
        </>
      )}
    </section>
  );
};
